// src/services/contact/userSearchService.js

import { auth, db } from '../../firebase';
import { collection, query, where, getDocs, limit, doc, getDoc } from 'firebase/firestore';

class UserSearchService {
  // ── Singleton ──────────────────────────────────────────────────────────────

  constructor() {
    this._db = db;
    this._auth = auth;
  }

  static getInstance() {
    if (!UserSearchService._instance) {
      UserSearchService._instance = new UserSearchService();
    }
    return UserSearchService._instance;
  }

  // ── Search users by phone numbers ─────────────────────────────────────────

  async searchUsersByPhones({ phoneNumbersA, phoneNumbersC, token }) {
    let phonesToSend;

    if (phoneNumbersC && phoneNumbersC.length > 0) {
      phonesToSend = phoneNumbersC;
    } else if (phoneNumbersA && phoneNumbersA.length > 0) {
      phonesToSend = phoneNumbersA.join('|');
    } else {
      return {};
    }

    return this._searchUsersByPhonesFirestore(phonesToSend);
  }

  // ── Search users by phone numbers directly in Firestore ───────────────────

  async _searchUsersByPhonesFirestore(phonesToSend) {
    const phoneNumbers = phonesToSend.split('|');
    const result = {};

    try {
      // Firestore 'in' query limited to 10 items
      const chunkSize = 10;
      
      for (let i = 0; i < phoneNumbers.length; i += chunkSize) {
        const chunk = phoneNumbers.slice(i, i + chunkSize);
        
        const q = query(
          collection(this._db, 'users'),
          where('phone', 'in', chunk)
        );
        
        const querySnap = await getDocs(q);
        
        for (const doc of querySnap.docs) {
          const data = doc.data();
          const phone = data.phone;
          if (phone) {
            result[phone] = doc.id;
          }
        }
      }
      
      return result;
    } catch (error) {
      console.error('Firestore search error:', error);
      return {};
    }
  }

  // ── Search users by username ──────────────────────────────────────────────

  async searchUsersByUsername(username) {
    if (!username || username.length < 2) return [];

    try {
      const q = query(
        collection(this._db, 'users'),
        where('username', '>=', username.toLowerCase()),
        where('username', '<=', username.toLowerCase() + '\uf8ff'),
        limit(20)
      );
      
      const querySnap = await getDocs(q);
      const results = [];

      for (const doc of querySnap.docs) {
        const data = doc.data();
        results.push({
          uid: doc.id,
          username: data.username,
          name: data.name,
          phone: data.phone,
          profileImage: data.profileImage || '',
          isVerified: data.isVerified || false,
        });
      }

      return results;
    } catch (error) {
      console.error('searchUsersByUsername error:', error);
      return [];
    }
  }

  // ── Search users by name ──────────────────────────────────────────────────

  async searchUsersByName(name) {
    if (!name || name.length < 2) return [];

    try {
      // Firestore doesn't support partial text search natively
      // Use a combination of queries
      const q = query(
        collection(this._db, 'users'),
        where('name', '>=', name.toLowerCase()),
        where('name', '<=', name.toLowerCase() + '\uf8ff'),
        limit(20)
      );
      
      const querySnap = await getDocs(q);
      const results = [];

      for (const doc of querySnap.docs) {
        const data = doc.data();
        results.push({
          uid: doc.id,
          username: data.username,
          name: data.name,
          phone: data.phone,
          profileImage: data.profileImage || '',
          isVerified: data.isVerified || false,
        });
      }

      return results;
    } catch (error) {
      console.error('searchUsersByName error:', error);
      return [];
    }
  }

  // ── Search users by phone (single) ────────────────────────────────────────

  async searchUserByPhone(phone) {
    if (!phone) return null;

    try {
      const normalized = this._normalizePhone(phone);
      
      const q = query(
        collection(this._db, 'users'),
        where('phone', '==', normalized),
        limit(1)
      );
      
      const querySnap = await getDocs(q);
      
      if (querySnap.empty) return null;
      
      const doc = querySnap.docs[0];
      const data = doc.data();
      
      return {
        uid: doc.id,
        username: data.username,
        name: data.name,
        phone: data.phone,
        profileImage: data.profileImage || '',
        isVerified: data.isVerified || false,
      };
    } catch (error) {
      console.error('searchUserByPhone error:', error);
      return null;
    }
  }

  // ── Helper: Normalize phone number ────────────────────────────────────────

  _normalizePhone(phone) {
    if (!phone) return '';
    
    let cleaned = phone.replace(/[^\d+]/g, '');

    if (cleaned.startsWith('0')) {
      cleaned = `+256${cleaned.substring(1)}`;
    }

    if (cleaned.startsWith('256') && !cleaned.startsWith('+')) {
      cleaned = `+${cleaned}`;
    }

    if (!cleaned.startsWith('+')) {
      cleaned = `+${cleaned}`;
    }

    return cleaned;
  }

  // ── Get user by ID ─────────────────────────────────────────────────────────

  async getUserById(uid) {
    if (!uid) return null;

    try {
      const docRef = doc(this._db, 'users', uid);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) return null;
      
      const data = docSnap.data();
      
      return {
        uid: docSnap.id,
        username: data.username,
        name: data.name,
        phone: data.phone,
        profileImage: data.profileImage || '',
        isVerified: data.isVerified || false,
        bio: data.bio || '',
        stats: data.stats || {},
      };
    } catch (error) {
      console.error('getUserById error:', error);
      return null;
    }
  }

  // ── Get multiple users by IDs ─────────────────────────────────────────────

  async getUsersByIds(uids) {
    if (!uids || uids.length === 0) return [];

    try {
      const results = [];
      const chunkSize = 10;

      for (let i = 0; i < uids.length; i += chunkSize) {
        const chunk = uids.slice(i, i + chunkSize);
        const q = query(
          collection(this._db, 'users'),
          where('uid', 'in', chunk)
        );
        const querySnap = await getDocs(q);
        
        for (const doc of querySnap.docs) {
          const data = doc.data();
          results.push({
            uid: doc.id,
            username: data.username,
            name: data.name,
            phone: data.phone,
            profileImage: data.profileImage || '',
            isVerified: data.isVerified || false,
          });
        }
      }

      return results;
    } catch (error) {
      console.error('getUsersByIds error:', error);
      return [];
    }
  }

  // ── Dispose ─────────────────────────────────────────────────────────────────

  dispose() {
    // Clean up any resources if needed
  }
}

// ── Singleton export ─────────────────────────────────────────────────────────

const userSearchService = UserSearchService.getInstance();
export { userSearchService };
export default userSearchService;